import { useIntl } from "react-intl";
import { Tooltip, Switch } from "antd";
import { FaRegTrashAlt } from "react-icons/fa";
import { MdError } from "react-icons/md";
import ControlledInput from "../../../../components/ControlledInputs/input";
import ControlledSelect from "../../../../components/ControlledInputs/select";
import TranslationField from "./TranslationField";
import { getNestedError, formatOptions } from "../../../../utils/flowUtils";
import {
  FileSection,
  FileItem,
  IndexCountFiles,
  FileRowContainer,
  FlowNameContainer,
  CountryFlagIcon,
  AddNewTranslate,
  SwitchesWrapper,
  SwitchContainer,
  RemoveFileButton,
  AddFileButton,
  InfoProductsECommerce,
  DocumentsErrorMessage,
} from "../styles";
import brazilFlag from "../../../../assets/images/brazil-flag.png";
import ukFlag from "../../../../assets/images/uk-flag.png";

/**
 * Componente para gerenciar a lista de documentos
 * Mantém toda a funcionalidade original de adicionar/remover/validar documentos
 */
const DocumentsList = ({
  fields,
  control,
  formState,
  watch,
  watchedFiles,
  setValue,
  form,
  fileAvailableTranslations,
  handleAddFileTranslation,
  handleRemoveFileTranslation,
  handleAddFile,
  handleRemoveFile,
  ecommerceProducts = [],
  inactivate,
  isDocumentPackage = false,
}) => {
  const intl = useIntl();

  return (
    <FileSection>
      {fields.map((field, index) => {
        // Calcular se deve desabilitar o botão baseado no estado atual das traduções deste arquivo
        const fileNameTranslations = watch(
          `documents.${index}.fileNameTranslations`
        );
        const hasFileTranslations =
          fileNameTranslations && Object.keys(fileNameTranslations).length > 0;
        const shouldDisableFileButton = hasFileTranslations;

        return (
          <FileItem key={`${field.id}-${index}`}>
            <IndexCountFiles>
              <span>
                {intl.formatMessage({ id: "admin.name_indice_count_file" })}{" "}
                {index + 1}
              </span>
            </IndexCountFiles>

            <FileRowContainer>
              <FlowNameContainer>
                <CountryFlagIcon
                  src={
                    intl.locale === "en" || intl.locale === "en-US"
                      ? ukFlag
                      : brazilFlag
                  }
                  alt="country icon"
                  hasLabel={true}
                />
                <div style={{ width: "100%" }}>
                  <ControlledInput
                    data-test={`file-name-input-${index}`}
                    disabled={inactivate}
                    key={`fileName-${field.id}-${index}`}
                    name={`documents.${index}.fileName`}
                    control={control}
                    formState={formState}
                    label="file_name"
                    placeholder={intl.formatMessage({ id: "file_name" })}
                    required={true}
                    arrayName="documents"
                    max={100}
                    width="100%"
                  />
                </div>
              </FlowNameContainer>

              {/* Campos de tradução dos arquivos */}
              {watch(`documents.${index}.fileNameTranslations`) &&
                Object.keys(
                  watch(`documents.${index}.fileNameTranslations`)
                ).map((translationKey) => (
                  <TranslationField
                    data-test={`file-name-translation-${index}-${translationKey}`}
                    inactivate={inactivate}
                    key={`${field.id}-${index}-${translationKey}`}
                    name={`documents.${index}.fileNameTranslations.${translationKey}`}
                    control={control}
                    formState={formState}
                    translationKey={translationKey}
                    onRemove={(key) =>
                      handleRemoveFileTranslation(index, key, form)
                    }
                    placeholder={intl.formatMessage({ id: "file_name" })}
                  />
                ))}

              {/* Botão para adicionar tradução */}
              <AddNewTranslate
                type="button"
                data-test={`add-translation-document-${index}`}
                onClick={() =>
                  !shouldDisableFileButton &&
                  fileAvailableTranslations[index]?.length > 0 &&
                  handleAddFileTranslation(
                    index,
                    fileAvailableTranslations[index][0],
                    form
                  )
                }
                disabled={shouldDisableFileButton || inactivate}
                style={{ marginTop: "8px", alignSelf: "flex-start" }}
              >
                {intl.formatMessage({ id: "add_another_translation" })}
              </AddNewTranslate>

              {/* Seleção de formatos */}
              <div className="format-wrapper">
                <ControlledSelect
                  data-test={`file-format-select-${index}`}
                  disabled={inactivate}
                  key={`fileTypes-${field.id}-${index}`}
                  name={`documents.${index}.allowedFileTypes`}
                  control={control}
                  formState={formState}
                  label="FORMAT_DOCUMENTS"
                  placeholder={intl.formatMessage({ id: "select" })}
                  options={formatOptions}
                  required={true}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  errors={{
                    [`documents.${index}.allowedFileTypes`]: getNestedError(
                      `documents.${index}.allowedFileTypes`,
                      formState
                    ),
                  }}
                  width="100%"
                />
              </div>

              {/* Seleção de produtos do e-commerce */}
              <div className="format-wrapper products-ecommerce">
                <ControlledSelect
                  data-test={`products-ecommerce-select-${index}`}
                  disabled={inactivate}
                  key={`productsEcommerce-${field.id}-${index}`}
                  name={`documents.${index}.productsEcommerce`}
                  control={control}
                  formState={formState}
                  label="products_available_ecommerce"
                  placeholder={intl.formatMessage({ id: "select" })}
                  options={
                    ecommerceProducts.length > 0 ? ecommerceProducts : []
                  }
                  required={false}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  errors={{
                    [`documents.${index}.productsEcommerce`]: getNestedError(
                      `documents.${index}.productsEcommerce`,
                      formState
                    ),
                  }}
                  width="100%"
                />
                <InfoProductsECommerce>
                  {intl.formatMessage({
                    id: "products_available_ecommerce_info",
                  })}
                </InfoProductsECommerce>
              </div>

              {/* Switches lado a lado */}
              <SwitchesWrapper>
                <SwitchContainer>
                  <span>{intl.formatMessage({ id: "REQUIRED_FILE" })}</span>
                  <Switch
                    data-test={`mandatory-switch-${index}`}
                    disabled={inactivate || isDocumentPackage}
                    key={`mandatory-${field.id}-${index}`}
                    checked={watchedFiles?.[index]?.mandatory || false}
                    onChange={(checked) => {
                      setValue(`documents.${index}.mandatory`, checked);
                    }}
                  />
                </SwitchContainer>
                <SwitchContainer>
                  <span>
                    {intl.formatMessage({ id: "INSURANCE_DASHBOARD" })}
                  </span>
                  <Switch
                    data-test={`insurance-switch-${index}`}
                    disabled={inactivate}
                    key={`insurance-${field.id}-${index}`}
                    checked={watchedFiles?.[index]?.insuranceDashboard || false}
                    onChange={(checked) => {
                      setValue(
                        `documents.${index}.insuranceDashboard`,
                        checked
                      );
                    }}
                  />
                </SwitchContainer>
              </SwitchesWrapper>
            </FileRowContainer>

            {/* Botão remover arquivo */}
            {fields.length > 1 && (
              <RemoveFileButton
                data-test={`remove-document-${index}`}
                disabled={inactivate}
                type="button"
                onClick={() =>
                  handleRemoveFile(
                    index,
                    field.documentConfigId,
                    field.hasExecutions
                  )
                }
              >
                <Tooltip title={intl.formatMessage({ id: "remove_file" })}>
                  <FaRegTrashAlt size={18} />
                </Tooltip>
              </RemoveFileButton>
            )}
          </FileItem>
        );
      })}

      {/* Botão adicionar arquivo */}
      <AddFileButton
        data-test="add-document-file"
        disabled={inactivate}
        type="button"
        onClick={handleAddFile}
      >
        {intl.formatMessage({ id: "ADD_ANOTHER_FILE" })}
      </AddFileButton>

      {/* Exibir erros de validação de pacote de documentos */}
      {formState.errors.documents?.message && (
        <DocumentsErrorMessage role="alert">
          <MdError color="#DC2626" size={14} />
          {intl.formatMessage({
            id: formState.errors.documents.message,
          })}
        </DocumentsErrorMessage>
      )}
    </FileSection>
  );
};

export default DocumentsList;
